import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { Request } from '@/domain/requests'
import {
  useDeclineRequest,
  useCancelRequest,
  useRemindRequest,
  useTakeBackRequest,
} from '@/domain/requests'
import { useUserStore } from '@/store/useUserStore'
import { ApiErrorCode } from '@/api/apiTypes'
import { mapAxiosErrorToApiError } from '@/utils'

interface UseRequestItemHandlerParams {
  request: Request
  onCancelSuccess?: () => void
  byDetailsScreen?: boolean
  enabled?: boolean
}

interface FormattedRequestItem {
  title: string
  userFullName: string
  isRequestForMe: boolean
  isPaymentLink: boolean
  isPending: boolean
  cancelLabel: string
  nextLabel: string
  avatar: {
    uri?: string
    name: string
    colors?: string[]
  }
}

export const useRequestItemHandler = ({
  request,
  onCancelSuccess,
  byDetailsScreen = false,
  enabled = true,
}: UseRequestItemHandlerParams) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Get current user ID from store
  const currentUserId = useUserStore(
    (state) => state.userAuthenticated?.user_id,
  )

  // Always call hooks (React rules)
  const { mutateAsync: declineRequest, isPending: isPendingDecline } =
    useDeclineRequest()
  const { mutateAsync: cancelRequest, isPending: isPendingCancel } =
    useCancelRequest()
  const { mutateAsync: remindRequest, isPending: isPendingRemind } =
    useRemindRequest()
  const { mutateAsync: takeBackRequest, isPending: isPendingTakeBack } =
    useTakeBackRequest()

  // Calculate loading states
  const isLoadingCancel =
    isPendingCancel || isPendingDecline || isPendingTakeBack
  const isLoadingNext = isPendingRemind

  // Determine request characteristics
  const isPaymentLink =
    enabled && request ? request.request_type === 'pending_request' : false
  const isPending = enabled && request ? request.state === 'pending' : false

  // Determine if this is an incoming request (request for me)
  const isRequestForMe =
    enabled && request && !isPaymentLink
      ? request.acceptor.id === currentUserId
      : false

  // Format request item for display
  const formattedRequest: FormattedRequestItem = useMemo(() => {
    // Early return if not enabled or no request
    if (!enabled || !request) {
      return {
        title: '',
        userFullName: '',
        isRequestForMe: false,
        isPaymentLink: false,
        isPending: false,
        cancelLabel: '',
        nextLabel: '',
        avatar: { name: '', colors: ['#6366F1', '#8B5CF6'] },
      }
    }

    // Payment link: no real acceptor
    if (isPaymentLink) {
      return {
        title: byDetailsScreen
          ? t(
              'translation:paymentLinkCreatedByYou',
              'You created a payment link',
            )
          : t('transactions:types.paymentLink', 'Payment Link'),
        userFullName: t('transactions:types.paymentLink', 'Payment Link'),
        isRequestForMe: false,
        isPaymentLink: true,
        isPending,
        cancelLabel: t('translation:takeBack', 'Take back'),
        nextLabel: '',
        avatar: { name: 'PL', colors: ['#6366F1', '#8B5CF6'] },
      }
    }

    const { initiator, acceptor } = request

    const fullInitiatorName =
      `${initiator.first_name || ''} ${initiator.last_name || ''}`.trim()
    const fullAcceptorName =
      `${acceptor.first_name || ''} ${acceptor.last_name || ''}`.trim()

    const getUserInfo = () => {
      if (isRequestForMe) {
        const title = byDetailsScreen
          ? t('translation:youGotARequest', 'You got a request')
          : `${fullInitiatorName} ${t('translation:requestedYou', 'requested you')}`

        return {
          title,
          userFullName: fullInitiatorName,
          avatar: {
            uri: initiator.avatar_path || undefined,
            name: initiator.first_name || '',
            colors: !initiator.avatar_path
              ? ['#6366F1', '#8B5CF6']
              : undefined,
          },
        }
      }

      const title = byDetailsScreen
        ? t('translation:youSentARequest', 'You sent a request')
        : t('translation:youRequested', 'You requested')

      return {
        title,
        userFullName:
          fullAcceptorName || acceptor.phone_number || 'Unknown',
        avatar: {
          uri: acceptor.avatar_path || undefined,
          name: acceptor.first_name || 'U',
          colors: !acceptor.avatar_path ? ['#6366F1', '#8B5CF6'] : undefined,
        },
      }
    }

    const { title, userFullName, avatar } = getUserInfo()

    const cancelLabel = isRequestForMe
      ? t('form:actions.decline', 'Decline')
      : t('form:actions.cancel', 'Cancel')

    const nextLabel = isRequestForMe
      ? t('form:actions.send', 'Send')
      : t('form:actions.remind', 'Remind')

    return {
      title,
      userFullName,
      isRequestForMe,
      isPaymentLink: false,
      isPending,
      cancelLabel,
      nextLabel,
      avatar,
    }
  }, [request, isRequestForMe, isPaymentLink, isPending, byDetailsScreen, t, enabled])

  // Handle cancel/decline/take-back action
  const handleCancel = useCallback(async () => {
    try {
      if (isPaymentLink) {
        await takeBackRequest({ requestId: request.id })
      } else if (isRequestForMe) {
        await declineRequest({ requestId: request.id })
      } else {
        await cancelRequest({ requestId: request.id })
      }
      onCancelSuccess?.()
    } catch (_error) {
      toast.error(t('api:errors.somethingWrong'))
    }
  }, [
    isPaymentLink,
    isRequestForMe,
    request.id,
    takeBackRequest,
    declineRequest,
    cancelRequest,
    onCancelSuccess,
    t,
  ])

  // Handle accept/remind action (no-op for payment links)
  const handleNext = useCallback(async () => {
    if (isPaymentLink) return

    if (isRequestForMe) {
      const recipientParams = {
        id: request.initiator.id,
        name: `${request.initiator.first_name || ''} ${
          request.initiator.last_name || ''
        }`.trim(),
        username: request.initiator.username || '',
        avatar: request.initiator.avatar_path,
        type: 'send' as const,
        currencyValue: request.amount?.amount || '0',
        currencyCode: request.amount?.currency || 'USDT',
        fiatValue: request.amount?.fiat_amount || '0',
        fiatCode: request.amount?.fiat_currency || 'USD',
        phoneNumber: request.initiator.phone_number || '',
        note: request.note || '',
        requestId: request.id,
        byApp: 'true',
      }

      navigate('/send-request/recipient', {
        state: recipientParams,
      })
      return
    }

    // For outgoing requests, send remind
    try {
      await remindRequest({ requestId: request.id })
      toast.success(t('translation:userReminded'))
    } catch (error) {
      const { code } = mapAxiosErrorToApiError(error)

      if (code === ApiErrorCode.TOO_MANY_ATTEMPTS) {
        toast.error(t('api:errors.limitOnePerDay'))
        return
      }

      toast.error(t('api:errors.somethingWrong'))
    }
  }, [isPaymentLink, isRequestForMe, request, remindRequest, navigate, t])

  return {
    formattedRequest,
    handleCancel,
    handleNext,
    isLoadingCancel,
    isLoadingNext,
  }
}
